import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTreatmentZones } from '../hooks/useTreatmentZones';
import { ArrowLeft, ArrowRight } from './icons';
import useEmblaCarousel from 'embla-carousel-react';
import { apiService, getCurrentLocale } from '../services/api';
import { getBestImageUrl } from '../utils/imageUrl';

const useIsMobile = (query: string = '(max-width: 768px)') => {
  const isBrowser = typeof window !== 'undefined';
  
  const [isMobile, setIsMobile] = useState(isBrowser ? window.matchMedia(query).matches : false);

  useEffect(() => {
    if (!isBrowser) return;
    
    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);
    handleChange();

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query, isBrowser]);

  return isMobile;
};

interface TreatmentZone {
  id: number;
  documentId: string;
  name: string;
  slug?: string;
  description: string;
  benefits?: string[];
  order: number;
  ctaButton?: {
    id: number;
    text: string;
    url: string;
    variant: string;
    isExternal: boolean;
  };
  image?: {
    id: number;
    url: string;
    alternativeText?: string;
    formats?: {
      large?: { url: string; width: number; height: number; };
      medium?: { url: string; width: number; height: number; };
      small?: { url: string; width: number; height: number; };
      thumbnail?: { url: string; width: number; height: number; };
    };
    width?: number;
    height?: number;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

const TreatmentAreas: React.FC = () => {
  const { data, loading, error } = useTreatmentZones();
  const locale = getCurrentLocale();
  const isMobile = useIsMobile();
  const isRTL = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: isRTL ? 'rtl' : 'ltr',
    align: 'start',
    loop: false,
    skipSnaps: false,
    dragFree: false,
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  // Default content when no Strapi data is available
  const defaultTreatmentZones = [
    {
      title: "Wenkbrauwen",
      image: "/uploads/home/wenkbrauw.png"
    },
    {
      title: "Baard en snor",
      image: "/uploads/home/baard-snor.png"
    },
    {
      title: "Hoofdhuid",
      image: "/uploads/home/hoofdhuid.png"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Navigation functions
  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  // Check if we can scroll further
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Filter out duplicates from Strapi data
  const getUniqueTreatmentZones = (zones: TreatmentZone[] | null) => {
    if (!zones || zones.length === 0) return defaultTreatmentZones;
    
    // Sort zones by order field
    const sortedZones = [...zones].sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const seenNames = new Set();
    const uniqueZones = sortedZones.filter(zone => {
      if (seenNames.has(zone.name)) {
        return false;
      }
      seenNames.add(zone.name);
      return true;
    });
    
    return uniqueZones.length > 0 ? uniqueZones : defaultTreatmentZones;
  };

  if (loading) {
    return (
      <section className="treatment-areas" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="treatment-areas-container">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
            <div className="loading-text"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="treatment-areas" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="treatment-areas-container">
          <div className="treatment-areas-header">
            <h2 className="treatment-areas-title">
              Behandelzones
            </h2>
            <div className="treatment-areas-nav">
              <button
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                className="treatment-areas-nav-btn"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canScrollNext}
                className="treatment-areas-nav-btn"
              >
                <ArrowRight size={24} />
              </button>
            </div>
          </div>
          <div className="treatment-areas-carousel-container">
            <div ref={emblaRef} className="treatment-areas-carousel">
              <div className="treatment-areas-carousel-slides">
                {defaultTreatmentZones.map((zone, index) => (
                  <div 
                    key={index} 
                    className="treatment-zone"
                  >
                    <img 
                      src={zone.image} 
                      alt={zone.title}
                      className="treatment-zone-image"
                    />
                    <h3 className="treatment-zone-title">
                      {zone.title}
                    </h3>
                    <a 
                      href="/behandelzones" 
                      className="treatment-zone-link"
                    >
                      Lees meer {locale === 'ar' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const treatmentZones = getUniqueTreatmentZones(data);

  return (
    <motion.section
      className="treatment-areas"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="treatment-areas-container">
        <div className="treatment-areas-header">
          <h2 className="treatment-areas-title">
            Behandelzones
          </h2>
          <div className="treatment-areas-nav">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="treatment-areas-nav-btn"
            >
              <ArrowLeft size={24} />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="treatment-areas-nav-btn"
            >
              <ArrowRight size={24} />
            </button>
          </div>
        </div>
        
        <div className="treatment-areas-carousel-container">
          <div ref={emblaRef} className="treatment-areas-carousel">
            <div className="treatment-areas-carousel-slides">
              {treatmentZones.map((zone, index) => {
                // Handle both Strapi data structure and default data structure
                const isStrapiData = 'name' in zone && 'documentId' in zone;
                const title = isStrapiData ? zone.name : zone.title;
                
                let imageUrl: string | undefined;
                if (isStrapiData && zone.image) {
                  const best = getBestImageUrl(zone.image);
                  imageUrl = best === null ? undefined : best;
                } else if (!isStrapiData && 'image' in zone) {
                  imageUrl = (zone as any).image as string;
                }
                
                const finalImageUrl = imageUrl || "https://via.placeholder.com/379x430/cccccc/666666?text=Treatment+Zone";
                
                // Get CTA button data from Strapi or use default
                const ctaButton = isStrapiData && zone.ctaButton ? zone.ctaButton : null;
                const ctaUrl = ctaButton?.url || '/behandelzones';
                const ctaText = ctaButton?.text || 'Lees meer';
                
                return (
                  <motion.div 
                    key={isStrapiData ? zone.documentId : index}
                    className="treatment-zone"
                    variants={itemVariants}
                  >
                    <img 
                      src={finalImageUrl} 
                      alt={title}
                      className="treatment-zone-image"
                    />
                    <h3 className="treatment-zone-title">
                      {title}
                    </h3>
                    <a 
                      href={ctaUrl} 
                      className="treatment-zone-link"
                    >
                      {ctaText} {locale === 'ar' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                    </a>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default TreatmentAreas; 